import { Link } from "react-router-dom";

export type JobRow = {
  id: string;
  title: string;
  status: string;
  workflow_status?: string | null;
  job_type: string | null;
  shoot_date: string | null;
  client_name: string | null;
  created_at: string;
};

interface Props {
  jobs: JobRow[];
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  scheduled:        { label: "Scheduled",        color: "text-[#a6853e]",    bg: "bg-[#C8A96E]/10",    icon: "ri-calendar-event-line" },
  confirmed:        { label: "Confirmed",         color: "text-teal-600",     bg: "bg-teal-50",         icon: "ri-checkbox-circle-line" },
  shooting:         { label: "Shooting",          color: "text-amber-600",    bg: "bg-amber-100",       icon: "ri-camera-3-line" },
  editing:          { label: "Editing",           color: "text-slate-600",    bg: "bg-slate-100",       icon: "ri-edit-2-line" },
  waiting_client:   { label: "Waiting Client",    color: "text-orange-600",   bg: "bg-orange-100",      icon: "ri-user-voice-line" },
  rendering:        { label: "Rendering",         color: "text-teal-600",     bg: "bg-teal-100",        icon: "ri-loader-3-line" },
  delivered:        { label: "Delivered",         color: "text-emerald-600",  bg: "bg-emerald-100",     icon: "ri-send-plane-fill" },
  completed:        { label: "Completed",         color: "text-emerald-700",  bg: "bg-emerald-100",     icon: "ri-checkbox-circle-fill" },
  cancelled:        { label: "Cancelled",         color: "text-red-500",      bg: "bg-red-100",         icon: "ri-close-circle-line" },
  // legacy fallbacks
  pending:          { label: "Pending",           color: "text-slate-500",    bg: "bg-slate-100",       icon: "ri-time-line" },
  in_progress:      { label: "In Progress",       color: "text-amber-600",    bg: "bg-amber-100",       icon: "ri-loader-4-line" },
};

const JOB_TYPE_ICONS: Record<string, string> = {
  wedding:    "ri-heart-line",
  portrait:   "ri-user-smile-line",
  event:      "ri-calendar-event-line",
  commercial: "ri-building-line",
  family:     "ri-group-line",
  newborn:    "ri-heart-2-line",
  other:      "ri-camera-line",
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function DashboardJobs({ jobs }: Props) {
  const activeCount = jobs.filter((j) => !["completed", "cancelled", "delivered"].includes(j.status)).length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <span className="w-6 h-6 flex items-center justify-center rounded-lg bg-amber-50 text-amber-500">
            <i className="ri-camera-line text-xs"></i>
          </span>
          <h3 className="text-[#0f172a] font-semibold text-sm">Recent Jobs</h3>
          {activeCount > 0 && (
            <span className="bg-amber-100 text-amber-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {activeCount} active
            </span>
          )}
        </div>
        <Link to="/admin/jobs" className="text-[#C8A96E]/70 hover:text-[#C8A96E] text-xs transition-colors cursor-pointer whitespace-nowrap">
          View all →
        </Link>
      </div>

      {/* List */}
      <div className="flex-1 divide-y divide-slate-200">
        {jobs.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10">
            <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 text-slate-400">
              <i className="ri-camera-line text-lg"></i>
            </span>
            <p className="text-slate-400 text-sm">No jobs yet</p>
          </div>
        ) : (
          jobs.map((job) => {
            const effectiveStatus = job.workflow_status || job.status;
            const cfg = STATUS_CONFIG[effectiveStatus] ?? STATUS_CONFIG[job.status] ?? STATUS_CONFIG.pending;
            const typeIcon = JOB_TYPE_ICONS[job.job_type?.toLowerCase() ?? "other"] ?? "ri-camera-line";
            return (
              <Link
                key={job.id}
                to={`/admin/jobs/${job.id}`}
                className="px-5 py-3.5 flex items-center gap-3 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                {/* Type icon */}
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <i className={`${typeIcon} text-slate-400 text-sm`}></i>
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-[#0f172a] text-sm font-medium truncate">{job.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {job.client_name && (
                      <span className="text-slate-500 text-xs truncate">{job.client_name}</span>
                    )}
                    {job.shoot_date && (
                      <span className="text-slate-400 text-xs whitespace-nowrap">· {formatDate(job.shoot_date)}</span>
                    )}
                  </div>
                </div>
                {/* Status */}
                <span className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-lg shrink-0 ${cfg.bg} ${cfg.color}`}>
                  <i className={`${cfg.icon} text-[10px]`}></i>
                  {cfg.label}
                </span>
              </Link>
            );
          })
        )}
      </div>

      {/* Footer */}
      {jobs.length > 0 && (
        <div className="px-5 py-3 border-t border-white/5 shrink-0">
          <Link
            to="/admin/jobs"
            className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg text-xs font-medium text-slate-500 hover:text-[#0f172a] hover:bg-slate-50 border border-slate-200 hover:border-slate-300 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-line"></i>
            View all jobs
          </Link>
        </div>
      )}
    </div>
  );
}
